import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { RootLayout } from "@/components/layout/root-layout";

const LandingPage = lazy(() =>
  import("@/pages/landing").then((module) => ({ default: module.LandingPage })),
);
const LoginPage = lazy(() =>
  import("@/pages/login").then((module) => ({ default: module.LoginPage })),
);
const HomePage = lazy(() =>
  import("@/pages/dashboard/home").then((module) => ({
    default: module.HomePage,
  })),
);
const StudioPage = lazy(() =>
  import("@/pages/studio").then((module) => ({ default: module.StudioPage })),
);
const StudioPreviewPage = lazy(() =>
  import("@/pages/dashboard/studio-preview").then((module) => ({
    default: module.StudioPreviewPage,
  })),
);
const PersonaPage = lazy(() =>
  import("@/pages/dashboard/persona").then((module) => ({
    default: module.PersonaPage,
  })),
);
const PublishPage = lazy(() =>
  import("@/pages/dashboard/publish").then((module) => ({
    default: module.PublishPage,
  })),
);
const VisitorsPage = lazy(() =>
  import("@/pages/dashboard/visitors").then((module) => ({
    default: module.VisitorsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/dashboard/settings").then((module) => ({
    default: module.SettingsPage,
  })),
);
const StudioSitePreviewPage = lazy(() =>
  import("@/pages/studio-site-preview").then((module) => ({
    default: module.StudioSitePreviewPage,
  })),
);

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-warm-cream dark:bg-dark-ground gap-8">
      <div className="relative flex items-center justify-center w-24 h-24">
        {/* Outer expanding ring */}
        <div
          className="absolute inset-0 rounded-full bg-electric-lime/20 dark:bg-electric-lime/10 animate-ping"
          style={{ animationDuration: "2.5s" }}
        />
        {/* Inner pulsing ring */}
        <div
          className="absolute inset-4 rounded-full border-2 border-near-black/10 dark:border-electric-lime/30 animate-pulse"
          style={{ animationDuration: "1.5s" }}
        />
        {/* Solid core */}
        <div className="w-8 h-8 rounded-full bg-near-black dark:bg-electric-lime relative z-10 shadow-[0_0_15px_rgba(190,255,80,0.5)]" />
      </div>
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-graphite dark:text-dark-muted">
        LOADING...
      </p>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard/home" replace />;

  return <>{children}</>;
}

function StudioPreviewRoute({
  page,
}: {
  page: "home" | "work" | "blogs" | "case" | "chat";
}) {
  return <StudioSitePreviewPage page={page} />;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        <Route path="/landing" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/studio"
          element={
            <ProtectedRoute>
              <StudioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="home" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/work"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="work" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/blogs"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="blogs" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/work/:csSlug"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="case" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/case/:csSlug"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="case" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview/chat"
          element={
            <ProtectedRoute>
              <StudioPreviewRoute page="chat" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route path="studio" element={<StudioPreviewPage />} />
          <Route path="persona" element={<PersonaPage />} />
          <Route path="publish" element={<PublishPage />} />
          <Route path="visitors" element={<VisitorsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          {/* Legacy redirects so existing bookmarks keep working */}
          <Route
            path="agent"
            element={<Navigate to="/dashboard/home" replace />}
          />
          <Route
            path="agents"
            element={<Navigate to="/dashboard/home" replace />}
          />
          <Route
            path="embed"
            element={<Navigate to="/dashboard/publish" replace />}
          />
          <Route
            path="activity"
            element={<Navigate to="/dashboard/visitors" replace />}
          />
          <Route
            path="knowledge"
            element={<Navigate to="/dashboard/persona" replace />}
          />
          <Route
            path="integrations"
            element={<Navigate to="/dashboard/home" replace />}
          />
          <Route
            path="leads"
            element={<Navigate to="/dashboard/visitors" replace />}
          />
          <Route
            path="transcripts"
            element={<Navigate to="/dashboard/visitors" replace />}
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
